import { TableToolbar, TableFilter, SortableHeader } from '../components/TableControls'
import { matchesTableSearch, sortTableRows, useTableSort } from '../lib/tableControls'
import { SlackWorkspaceConnection } from '../components/SlackWorkspaceConnection'
import { OnboardingReturn } from '../components/OnboardingFlow'
import { WorkspaceMemberRoster } from '../components/TeamManagement/WorkspaceMemberRoster'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  Search,
  Settings,
  MoreHorizontal,
  UserRound,
  Archive,
  Folder,
  Pencil,
  Trash2,
  Plus,
  UserPlus,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'

import { supabase, signOut, getCurrentSession, removeTeamMember } from '../lib/supabase'
import { cn } from '../lib/utils'
import { AppSidebar } from '../components/AppSidebar'
import { TopBar } from '../components/TopBar'
import { ProjectsPanel } from '../components/TeamManagement/ProjectsPanel'
import { SidebarInset, SidebarProvider } from '../components/ui/sidebar'
import { Skeleton } from '../components/ui/skeleton'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar'
import { MemberPicker, MemberMoveConfirmation } from '../components/TeamManagement/MemberPicker'
import { AddSlackMembersSheet } from '../components/TeamManagement/AddSlackMembersSheet'
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '../components/ui/sheet'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'
import { buttonVariants } from '../components/ui/button'

// ============================================================================
// Page: /dashboard/team/:teamId/team-management
//
// Replaces the old Team Management modal (TeamManagement/TeamSettings.tsx).
// "Teams" here are `pods` (see 20260905180000_add_pods.sql) — a sub-grouping
// within this one Slack workspace — rebranded "Teams" in this UI per
// explicit product decision, 2026-09-05. Standup Config and Billing, former
// tabs of the old modal, are now their own sidebar pages (TeamConfig.tsx,
// TeamBilling.tsx).
//
// Invites reuse the existing Slack-picker mechanism (invite-team-members),
// not a new email-based flow — also an explicit decision, since the
// alternative (team_invitations + send-team-invitation) is dead code today
// with a real token bug. That means the "enter email to invite" look from
// the Figma is adapted here into a type-to-filter search over this team's
// Slack workspace members (via get-slack-users), not a free-text email
// field — someone has to already be in the connected Slack workspace to be
// added. A newly-added person's permission (role) and job title are stored
// as pending on `users` (pod_id, title, pending_role) and only become a
// real `team_members` row once they actually sign in — see
// setupSlackUserAndTeam in src/lib/supabase.ts for the consuming side. So
// their Status here reads "Pending sign-in" rather than the Figma's
// "Invited" (no email was sent) until that happens, then "Active".
// ============================================================================

interface TeamManagementProps {
  teamId: string
}

type TeamRole = 'owner' | 'admin' | 'member' | 'manager'
const PAGE_SIZE = 8

interface Pod {
  id: string
  name: string
  description: string | null
  archived_at: string | null
}

interface CombinedUser {
  id: string
  user_id: string
  auth_user_id: string | null
  role: 'owner' | 'admin' | 'member' | 'manager'
  joined_at: string
  user_name: string | null
  user_email: string | null
  slack_user_id: string | null
  is_slack_only: boolean
  can_manage: boolean
  pod_id: string | null
  title: string | null
  avatar_url: string | null
}

interface AgentInfo {
  id: string
  user_id: string
}

const TEAM_AVATAR_PALETTE = [
  'bg-pink-500',
  'bg-blue-500',
  'bg-emerald-600',
  'bg-violet-600',
  'bg-amber-500',
  'bg-cyan-600',
]

function teamPaletteFor(seed: string): string {
  let hash = 0
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  return TEAM_AVATAR_PALETTE[hash % TEAM_AVATAR_PALETTE.length]
}

function displayName(m: Pick<CombinedUser, 'user_name' | 'user_email'>): string {
  return m.user_name || m.user_email?.split('@')[0] || 'Unknown user'
}

function initialsOf(name: string): string {
  return (
    name
      .split(' ')
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  )
}

const TeamManagement: React.FC<TeamManagementProps> = ({ teamId }) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [teamName, setTeamName] = useState<string | undefined>(undefined)
  const [userRole, setUserRole] = useState<TeamRole>('member')

  const [pods, setPods] = useState<Pod[]>([])
  const [members, setMembers] = useState<CombinedUser[]>([])
  const [agents, setAgents] = useState<AgentInfo[]>([])
  // pod_managers rows for this team's pods -- who manages which pod. Fetched
  // separately from the main Promise.all below because it's scoped by the
  // pod ids that Promise.all itself just fetched (see loadAll).
  const [podManagers, setPodManagers] = useState<{ pod_id: string; auth_user_id: string }[]>([])
  const [togglingManagerId, setTogglingManagerId] = useState<string | null>(null)
  const [pendingInviteCount, setPendingInviteCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [selectedPodId, setSelectedPodId] = useState<string | null>(null)
  const [teamSearch, setTeamSearch] = useState('')
  const [page, setPage] = useState(0)
  const [memberSearch, setMemberSearch] = useState('')
  const [memberRole, setMemberRole] = useState('all')
  const [memberStatus, setMemberStatus] = useState('all')
  const { sort, toggleSort: toggle } = useTableSort('joined')
  const toggleSort = (key: string) => { toggle(key); setPage(0) }
  useEffect(() => { setPage(0) }, [memberSearch, memberRole, memberStatus])
  const [activeTab, setActiveTab] = useState<'members' | 'projects'>('members')
  const [createProjectRequest, setCreateProjectRequest] = useState(0)
  // Populated by ProjectsPanel's onCountChange as soon as it loads, even
  // before the user clicks into the Projects tab, so the tab badge is
  // correct on first render.
  const [podProjectCounts, setPodProjectCounts] = useState<Map<string, number>>(new Map())

  const [invitePodId, setInvitePodId] = useState<string | null>(null)

  const [createOpen, setCreateOpen] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createDescription, setCreateDescription] = useState('')
  const [createSelectedIds, setCreateSelectedIds] = useState<Set<string>>(new Set())
  const [creating, setCreating] = useState(false)
  const [createMoveConfirmed, setCreateMoveConfirmed] = useState(false)

  const [renameTarget, setRenameTarget] = useState<Pod | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [renameDescription, setRenameDescription] = useState('')
  const [editMemberIds, setEditMemberIds] = useState<Set<string>>(new Set())
  const [renaming, setRenaming] = useState(false)
  const [editMoveConfirmed, setEditMoveConfirmed] = useState(false)

  const [deleteTarget, setDeleteTarget] = useState<Pod | null>(null)
  const [deletingTeam, setDeletingTeam] = useState(false)

  const [removeTarget, setRemoveTarget] = useState<CombinedUser | null>(null)
  const [removingMember, setRemovingMember] = useState(false)
  const [roleUpdatingId, setRoleUpdatingId] = useState<string | null>(null)
  const [transferTargetAuthId, setTransferTargetAuthId] = useState('')
  const [transferringOwnership, setTransferringOwnership] = useState(false)

  // ROLE AUDIT (2026-10-09): admin-only, not admin+manager. This page is
  // team-structure governance -- create/rename/delete pod, invite, remove
  // member, change role, assign manager -- not pod-level oversight, so it
  // matches BillingManagement.tsx's own explicit admin-only reasoning rather
  // than the admin-or-manager pattern used for cost/usage/checkin data
  // (AgentsCostPanel.tsx, NewDashboard.tsx). Also: granting the manager role
  // itself has to stay admin-exclusive, or a manager could promote peers.
  const isOwner = userRole === 'owner'
  const isAdmin = isOwner || userRole === 'admin'

  const loadAll = async () => {
    setLoading(true)
    setError(null)
    try {
      const session = await getCurrentSession()
      if (!session) throw new Error('No session found')

      const [teamRes, podsRes, usersRes, agentsRes, invitesRes] = await Promise.all([
        supabase.from('teams').select('name').eq('id', teamId).single(),
        (supabase.from as any)('pods').select('id, name, description, archived_at').eq('team_id', teamId),
        supabase.functions.invoke('get-all-team-users', {
          body: { team_id: teamId },
          headers: { Authorization: `Bearer ${session.access_token}` },
        }),
        (supabase.from as any)('agents_team_visible').select('id, user_id').eq('team_id', teamId),
        (supabase.from as any)('team_invitations')
          .select('id', { count: 'exact', head: true })
          .eq('team_id', teamId)
          .is('accepted_at', null)
          .gt('expires_at', new Date().toISOString()),
      ])

      if (teamRes.error) throw teamRes.error
      if (podsRes.error) throw podsRes.error
      if (usersRes.error) throw usersRes.error

      setTeamName(teamRes.data?.name ?? undefined)
      setPods((podsRes.data ?? []) as Pod[])
      setMembers((usersRes.data?.users ?? []) as CombinedUser[])
      setAgents((agentsRes.data ?? []) as AgentInfo[])
      setPendingInviteCount(invitesRes.count ?? 0)

      // pod_managers' SELECT policy is permissive (true) rather than
      // team-scoped -- same posture as pods_select -- so we scope the query
      // ourselves to this team's own pod ids rather than relying on RLS to
      // do it.
      const teamPodIds = (podsRes.data ?? []).map((p: Pod) => p.id)
      if (teamPodIds.length > 0) {
        const pmRes = await (supabase.from as any)('pod_managers')
          .select('pod_id, auth_user_id')
          .in('pod_id', teamPodIds)
        if (pmRes.error) throw pmRes.error
        setPodManagers(pmRes.data ?? [])
      } else {
        setPodManagers([])
      }

      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        const { data: tm } = await supabase
          .from('team_members')
          .select('role')
          .eq('team_id', teamId)
          .eq('auth_user_id', user.id)
          .single()
        if (tm?.role) setUserRole(tm.role as TeamRole)
      }

      setSelectedPodId((prev) => {
        if (prev && (podsRes.data ?? []).some((p: Pod) => p.id === prev)) return prev
        return podsRes.data?.[0]?.id ?? null
      })
    } catch (err) {
      console.error('Failed to load Team Management data:', err)
      setError('Could not load team management for this team.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAll()
  }, [teamId])

  useEffect(() => {
    setPage(0)
  }, [selectedPodId])

  // Reset to Members whenever the selected pod changes, so switching teams
  // doesn't leave you stranded on a Projects tab for a different pod.
  useEffect(() => {
    setActiveTab('members')
  }, [selectedPodId])

  const handleProjectCountChange = React.useCallback((podId: string, count: number) => {
    setPodProjectCounts((prev) => {
      if (prev.get(podId) === count) return prev
      const next = new Map(prev)
      next.set(podId, count)
      return next
    })
  }, [])

  const goToDashboard = () => navigate(`/dashboard/team/${teamId}`)
  const handleLogout = async () => {
    await signOut()
    navigate('/auth/login')
  }

  const podMemberCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const m of members) {
      if (!m.pod_id) continue
      counts.set(m.pod_id, (counts.get(m.pod_id) ?? 0) + 1)
    }
    return counts
  }, [members])

  const memberPodById = useMemo(() => {
    const map = new Map<string, string | null>()
    for (const m of members) map.set(m.user_id, m.pod_id)
    return map
  }, [members])

  const podAgentCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const a of agents) {
      const podId = memberPodById.get(a.user_id)
      if (!podId) continue
      counts.set(podId, (counts.get(podId) ?? 0) + 1)
    }
    return counts
  }, [agents, memberPodById])

  const filteredPods = useMemo(() => {
    if (!teamSearch.trim()) return pods
    const q = teamSearch.trim().toLowerCase()
    return pods.filter((p) => p.name.toLowerCase().includes(q))
  }, [pods, teamSearch])

  const selectedPod = pods.find((p) => p.id === selectedPodId) ?? null

  // auth_user_ids who manage the CURRENTLY SELECTED pod specifically. Not the
  // same thing as "role === 'manager'" -- that's a team-wide permission tier
  // (team_members.role) a person can hold while managing a *different* pod
  // than the one being viewed here. The Role column and the toggle button
  // below both read this (pod-scoped), not the raw role, so the label
  // always matches what the button is about to do.
  const selectedPodManagerIds = useMemo(() => {
    if (!selectedPod) return new Set<string>()
    return new Set(
      podManagers.filter((pm) => pm.pod_id === selectedPod.id).map((pm) => pm.auth_user_id)
    )
  }, [podManagers, selectedPod])

  const handleToggleManager = async (member: CombinedUser) => {
    if (!selectedPod || !member.auth_user_id) return
    const currentlyManages = selectedPodManagerIds.has(member.auth_user_id)
    setTogglingManagerId(member.id)
    try {
      const session = await getCurrentSession()
      if (!session) throw new Error('No session found')
      const action = currentlyManages ? 'unassign_manager' : 'assign_manager'
      const { data, error: fnError } = await supabase.functions.invoke('manage-pods', {
        body: {
          action,
          team_id: teamId,
          pod_id: selectedPod.id,
          manager_auth_user_id: member.auth_user_id,
        },
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (fnError) throw fnError
      if (data?.error) throw new Error(data.error)

      toast.success(
        currentlyManages
          ? `${displayName(member)} is no longer a manager of ${selectedPod.name}`
          : `${displayName(member)} is now a manager of ${selectedPod.name}`
      )
      await loadAll()
    } catch (err: any) {
      console.error('Failed to update manager assignment:', err)
      toast.error(err.message || 'Failed to update manager assignment')
    } finally {
      setTogglingManagerId(null)
    }
  }

  const podMembers = useMemo(() => selectedPod ? members.filter(m => m.pod_id === selectedPod.id) : [], [members, selectedPod])
  const filteredPodMembers = sortTableRows(podMembers.filter(m => matchesTableSearch(memberSearch, m.user_name, m.user_email, m.title)
    && (memberRole === 'all' || m.role === memberRole)
    && (memberStatus === 'all' || (memberStatus === 'active' ? !!m.auth_user_id : !m.auth_user_id))), sort, {
      name: displayName, role: m => m.role, joined: m => Date.parse(m.joined_at), status: m => m.auth_user_id ? 'Active' : 'Pending sign-in',
    })
  const totalPages = Math.max(Math.ceil(filteredPodMembers.length / PAGE_SIZE), 1)
  const currentPage = Math.min(page, totalPages - 1)
  const pageMembers = filteredPodMembers.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE)

  const memberOptions = members.map(member => ({
    id: member.user_id,
    name: displayName(member),
    email: member.user_email,
    avatar: member.avatar_url,
    description: [member.user_email, member.role.charAt(0).toUpperCase() + member.role.slice(1),
      member.pod_id ? `Currently in ${pods.find(pod => pod.id === member.pod_id)?.name || 'another team'}` : 'Unassigned'].filter(Boolean).join(' · '),
  }))
  const movesFor = (ids: Set<string>, targetId?: string) => members
    .filter(member => ids.has(member.user_id) && member.pod_id && member.pod_id !== targetId)
    .map(member => `${displayName(member)} — ${pods.find(pod => pod.id === member.pod_id)?.name || 'another team'}`)
  const createMoves = movesFor(createSelectedIds)
  const editMoves = movesFor(editMemberIds, renameTarget?.id)

  const openInviteForPod = (pod: Pod) => {
    if (isAdmin && !pod.archived_at) setInvitePodId(pod.id)
  }

  const handleConfirmRemoveMember = async () => {
    if (!removeTarget) return
    setRemovingMember(true)
    try {
      await removeTeamMember(removeTarget.id)
      toast.success('Member removed')
      setRemoveTarget(null)
      await loadAll()
    } catch (err: any) {
      toast.error(err.message || 'Failed to remove member')
    } finally {
      setRemovingMember(false)
    }
  }

  const handleUpdateMemberRole = async (member: CombinedUser, role: TeamRole) => {
    if (!member.auth_user_id || !selectedPod || roleUpdatingId || role === 'owner') return
    setRoleUpdatingId(member.id)
    try {
      const session = await getCurrentSession()
      if (!session) throw new Error('No session found')
      const { data, error: fnError } = await supabase.functions.invoke('manage-pods', {
        body: {
          action: 'update_role',
          team_id: teamId,
          pod_id: selectedPod.id,
          member_auth_user_id: member.auth_user_id,
          role,
        },
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (fnError) throw fnError
      if (data?.error) throw new Error(data.error)

      toast.success(`${displayName(member)} is now a ${role}`)
      await loadAll()
    } catch (err: any) {
      console.error('Failed to update member role:', err)
      toast.error(err.message || 'Failed to update member role')
    } finally {
      setRoleUpdatingId(null)
    }
  }

  const handleCreateTeam = async () => {
    if (creating || !createName.trim() || (createMoves.length && !createMoveConfirmed)) return
    setCreating(true)
    try {
      const session = await getCurrentSession()
      if (!session) throw new Error('No session found')
      const { data, error: fnError } = await supabase.functions.invoke('manage-pods', {
        body: {
          action: 'create',
          team_id: teamId,
          name: createName.trim(),
          description: createDescription.trim() || null,
          member_user_ids: Array.from(createSelectedIds),
        },
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (fnError) throw fnError
      if (data?.error) throw new Error(data.error)

      if (data?.warning) {
        toast.warning('Team created, but members could not be assigned', { description: data.warning })
      } else {
        toast.success('Team created successfully', { description: 'Your team is now active and ready to use.' })
      }
      setCreateOpen(false)
      setCreateName('')
      setCreateDescription('')
      setCreateSelectedIds(new Set())
      setCreateMoveConfirmed(false)
      await loadAll()
      if (data?.pod?.id) setSelectedPodId(data.pod.id)
    } catch (err: any) {
      console.error('Failed to create team:', err)
      toast.error(err.message || 'Failed to create team')
    } finally {
      setCreating(false)
    }
  }

  const openRenameTeam = (pod: Pod) => {
    setRenameTarget(pod)
    setRenameValue(pod.name)
    setRenameDescription(pod.description ?? '')
    setEditMoveConfirmed(false)
    setEditMemberIds(new Set(members.filter((member) => member.pod_id === pod.id).map((member) => member.user_id)))
  }

  const handleRenameSubmit = async () => {
    if (renaming || !renameTarget || (editMoves.length && !editMoveConfirmed)) return
    const name = renameValue.trim()
    if (!name) {
      setRenameTarget(null)
      return
    }
    setRenaming(true)
    try {
      const session = await getCurrentSession()
      if (!session) throw new Error('No session found')
      const { data, error: fnError } = await supabase.functions.invoke('manage-pods', {
        body: {
          action: 'update',
          team_id: teamId,
          pod_id: renameTarget.id,
          name,
          description: renameDescription.trim() || null,
          member_user_ids: Array.from(editMemberIds),
        },
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (fnError) throw fnError
      if (data?.error) throw new Error(data.error)
      toast.success('Team updated')
      setRenameTarget(null)
      await loadAll()
    } catch (err: any) {
      toast.error(err.message || 'Failed to update team')
      await loadAll()
    } finally {
      setRenaming(false)
    }
  }

  const handleConfirmDeleteTeam = async () => {
    if (!deleteTarget) return
    setDeletingTeam(true)
    try {
      const session = await getCurrentSession()
      if (!session) throw new Error('No session found')
      const { error: fnError } = await supabase.functions.invoke('manage-pods', {
        body: { action: 'delete', team_id: teamId, pod_id: deleteTarget.id },
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (fnError) throw fnError
      toast.success('Team deleted')
      setSelectedPodId(null)
      setDeleteTarget(null)
      await loadAll()
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete team')
    } finally {
      setDeletingTeam(false)
    }
  }

  const handleToggleArchiveTeam = async (pod: Pod) => {
    try {
      const session = await getCurrentSession()
      if (!session) throw new Error('No session found')
      const { data, error: fnError } = await supabase.functions.invoke('manage-pods', {
        body: {
          action: pod.archived_at ? 'unarchive' : 'archive',
          team_id: teamId,
          pod_id: pod.id,
        },
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (fnError) throw fnError
      if (data?.error) throw new Error(data.error)
      toast.success(pod.archived_at ? 'Team unarchived' : 'Team archived')
      await loadAll()
    } catch (err: any) {
      toast.error(err.message || 'Failed to update team archive status')
    }
  }

  const ownerMemberId = useMemo(() => members.find((m) => m.role === 'owner')?.id ?? null, [members])
  const adminCount = members.filter((m) => m.role === 'admin' || m.role === 'owner').length
  const adminMembers = members.filter((m) => m.role === 'admin' && !!m.auth_user_id)

  const handleTransferOwnership = async () => {
    if (!isOwner || !transferTargetAuthId || transferringOwnership) return
    setTransferringOwnership(true)
    try {
      const { error: transferError } = await (supabase as any).rpc('transfer_workspace_ownership', {
        p_team_id: teamId,
        p_target_auth_user_id: transferTargetAuthId,
      } as any)
      if (transferError) throw transferError
      toast.success('Workspace ownership transferred')
      setTransferTargetAuthId('')
      await loadAll()
    } catch (err: any) {
      toast.error(err.message || 'Failed to transfer ownership')
    } finally {
      setTransferringOwnership(false)
    }
  }

  return (
    <SidebarProvider>
      <AppSidebar activeSection="team-management" onNavigate={goToDashboard} teamId={teamId} />
      <SidebarInset className="min-w-0">
        <TopBar
          teamId={teamId}
          teamName={teamName}
          onLogout={handleLogout}
          sectionLabel="Team Management"
        />
        {/* flex column filling the remaining viewport height below TopBar, so the
            teams/members panel below can stretch to fill the screen instead of
            only being as tall as its own content (2026-09-29). */}
        <main className="flex flex-1 flex-col space-y-6 overflow-hidden bg-background p-4 sm:p-6">
          <OnboardingReturn teamId={teamId} />
          {isOwner && searchParams.get('setup') === '1' && <SlackWorkspaceConnection teamId={teamId} canInstall />}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground mb-1">Team management</h1>
              <p className="text-muted-foreground text-sm">
                Create teams, manage the default holding team, and assign people across teams
              </p>
            </div>
            {isAdmin && (
              <div className="flex flex-col gap-2 sm:flex-row">
                {/* Solid button, matching the Figma reference (2026-09-29) --
                    was outline. Same rounded-md corner radius as every other
                    button (Button's own default), not a full pill. */}
                <Button
                  variant="default"
                  className="gap-2 px-6"
                  onClick={() => setCreateOpen(true)}
                >
                  <Plus className="h-4 w-4" />
                  Create Team
                </Button>
                {isOwner && adminMembers.length > 0 && (
                  <Button variant="outline" onClick={() => setTransferTargetAuthId(adminMembers[0].auth_user_id ?? '')}>
                    Transfer ownership
                  </Button>
                )}
              </div>
            )}
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          {!error && (
            <>
              {/* Matches NewDashboard.tsx's KPI grid, not a row of separate
                  floating Cards: one bordered/rounded/shadow block with
                  hairline dividers between cells (see that file's own
                  comment for why -- same reasoning applies here: bg-card
                  reads as a glaringly white box against the cream
                  --background page tone, and four cards with gaps doesn't
                  match the Figma dashboard's single seamless strip). Each
                  cell is a plain div, not a Card, so it doesn't double up
                  its own border/shadow/background. */}
              {/* No border-radius, per Figma (2026-09-29) -- was rounded-lg. */}
              <div className="grid grid-cols-1 divide-y divide-border overflow-hidden border border-border bg-background shadow-sm sm:grid-cols-2 sm:divide-x sm:divide-y-0 lg:grid-cols-4">
                <div className="space-y-1 p-4 sm:p-5">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Total Team
                  </div>
                  <div className="text-2xl font-semibold text-foreground sm:text-3xl">
                    {loading ? <Skeleton className="h-7 w-12" /> : pods.length}
                  </div>
                  <div className="text-xs text-muted-foreground">Across your organization</div>
                </div>
                <div className="space-y-1 p-4 sm:p-5">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Total Members
                  </div>
                  <div className="text-2xl font-semibold text-foreground sm:text-3xl">
                    {loading ? <Skeleton className="h-7 w-12" /> : members.length}
                  </div>
                  <div className="text-xs text-muted-foreground">Active team members</div>
                </div>
                <div className="space-y-1 p-4 sm:p-5">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Pending Invites
                  </div>
                  <div className="text-2xl font-semibold text-foreground sm:text-3xl">
                    {loading ? <Skeleton className="h-7 w-12" /> : pendingInviteCount}
                  </div>
                  <div className="text-xs text-muted-foreground">Invitation awaiting actions</div>
                </div>
                <div className="space-y-1 p-4 sm:p-5">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Admin Users
                  </div>
                  <div className="text-2xl font-semibold text-foreground sm:text-3xl">
                    {loading ? <Skeleton className="h-7 w-12" /> : adminCount}
                  </div>
                  <div className="text-xs text-muted-foreground">Team administration</div>
                </div>
              </div>

              {/* Two separate panels with a real gap between them, per Figma
                  (2026-09-29) -- reverses the earlier "one seamless block"
                  treatment; each panel now carries its own border/bg/shadow. */}
              {!loading && isAdmin && <WorkspaceMemberRoster members={members} teams={pods} onViewTeam={setSelectedPodId} teamId={teamId} actorRole={userRole} onRoleSaved={loadAll} />}
              <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
                <div className="flex flex-col overflow-hidden border border-border bg-background shadow-sm">
                  <div className="space-y-3 p-4">
                    <div className="text-sm font-bold uppercase tracking-wide text-foreground">
                      Teams ({pods.length})
                    </div>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search teams..."
                        value={teamSearch}
                        onChange={(e) => setTeamSearch(e.target.value)}
                        className="pl-8"
                      />
                    </div>
                  </div>
                  {/* divide-y between rows, no rounded hover pill -- matches the
                      same divided-list pattern as TeamConfig.tsx's Menu panel. Each
                      row gets its own py-1.5 so there's visible breathing room around
                      the divider line, not just a hairline touching the next row
                      (2026-09-29, per Figma). */}
                  <div className="flex-1 overflow-y-auto divide-y divide-border border-y border-border">
                    {loading ? (
                      // Shape mirrors a loaded team row (square avatar, name +
                      // member-count line) so the shimmer reads as "teams
                      // incoming" rather than a generic bar.
                      <>
                        {[0, 1, 2, 3].map((i) => (
                          <div key={i} className="flex w-full min-h-[44px] items-center gap-3 px-2 py-1.5">
                            <Skeleton className="h-9 w-9 shrink-0 rounded-md" />
                            <div className="min-w-0 flex-1 space-y-2">
                              <Skeleton className="h-4 w-24" />
                              <Skeleton className="h-3 w-16" />
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      <>
                        {filteredPods.map((pod) => (
                          <div
                            key={pod.id}
                            className={`flex w-full min-h-[44px] items-center gap-1 px-2 py-1.5 transition-colors ${pod.archived_at ? 'opacity-60' : ''} ${
                              selectedPodId === pod.id ? 'bg-muted' : 'hover:bg-muted/50'
                            }`}
                          >
                            <button
                              type="button"
                              onClick={() => setSelectedPodId(pod.id)}
                              className="flex min-w-0 flex-1 items-center gap-3 p-2 text-left"
                            >
                              {/* Rounded-square avatar for a team/pod, distinct from the
                                  circular ones used for people (below and in the member
                                  table) -- matches the reference mock's distinction. */}
                              <Avatar className="h-9 w-9 shrink-0 rounded-md">
                                <AvatarFallback
                                  className={`rounded-md text-xs font-semibold text-white ${teamPaletteFor(pod.id)}`}
                                >
                                  {pod.name.slice(0, 2).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div className="min-w-0">
                                <div className="truncate text-sm font-medium text-foreground">
                                  {pod.name}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {podMemberCounts.get(pod.id) ?? 0} member
                                  {(podMemberCounts.get(pod.id) ?? 0) === 1 ? '' : 's'}
                                </div>
                              </div>
                            </button>
                            {isAdmin && (
                              // Same "..." = more actions -> delete mapping as the
                              // selected team's own header icon below, just scoped to
                              // this row's pod instead of whichever one is selected.
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 shrink-0 text-muted-foreground"
                                aria-label={`More actions for ${pod.name}`}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setDeleteTarget(pod)
                                }}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        {filteredPods.length === 0 && (
                          <p className="p-2 text-sm text-muted-foreground">No teams found.</p>
                        )}
                      </>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="border-t border-border p-2">
                      <Button
                        variant="ghost"
                        className="w-full justify-center gap-2 text-primary"
                        onClick={() => setCreateOpen(true)}
                      >
                        <Plus className="h-4 w-4" />
                        Create team
                      </Button>
                    </div>
                  )}
                </div>

                {/* flex column, not just a padded block -- so that when this panel's
                    content is shorter than the teams-list panel it's stretched
                    against (equal-height grid row), the pagination bar below still
                    lands flush on the panel's own bottom edge instead of leaving a
                    gap under it (2026-09-29). */}
                <div className="flex flex-col overflow-hidden border border-border bg-background shadow-sm">
                  <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
                    {loading ? (
                      // Shape mirrors the selected-team header (square avatar
                      // + name/subtitle) plus a few member rows (circular
                      // person avatar, name line, status pill) so the shimmer
                      // reads as "team detail incoming" rather than one blob.
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-10 w-10 shrink-0 rounded-md" />
                          <div className="space-y-2">
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-3 w-24" />
                          </div>
                        </div>
                        <div className="divide-y divide-border border-y border-border">
                          {[0, 1, 2, 3].map((i) => (
                            <div key={i} className="flex items-center justify-between gap-3 p-4">
                              <div className="flex items-center gap-3">
                                <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
                                <Skeleton className="h-4 w-28" />
                              </div>
                              <Skeleton className="h-6 w-20 shrink-0 rounded-full" />
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : !selectedPod ? (
                      <p className="text-sm text-muted-foreground">
                        Select or create a team to see its members.
                      </p>
                    ) : (
                      <>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10 shrink-0 rounded-md">
                              <AvatarFallback
                                className={`rounded-md text-sm font-semibold text-white ${teamPaletteFor(selectedPod.id)}`}
                              >
                                {selectedPod.name.slice(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="font-semibold text-foreground">{selectedPod.name}</div>
                              <div className="text-sm text-muted-foreground">
                                {podMemberCounts.get(selectedPod.id) ?? 0} members ·{' '}
                                {podAgentCounts.get(selectedPod.id) ?? 0} connectors
                              </div>
                            </div>
                          </div>
                          {isAdmin && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" aria-label="Team actions">
                                  <Settings className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-52">
                                <DropdownMenuItem onClick={() => openRenameTeam(selectedPod)}>
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Edit team
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  disabled={!!selectedPod.archived_at}
                                  title={selectedPod.archived_at ? 'Unarchive this team before inviting new members.' : undefined}
                                    onClick={() => openInviteForPod(selectedPod)}
                                >
                                  <UserPlus className="mr-2 h-4 w-4" />
                                  Invite user
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  disabled={!!selectedPod.archived_at}
                                  title={selectedPod.archived_at ? 'Unarchive this team before creating a project for it.' : undefined}
                                  onClick={() => {
                                    if (selectedPod.archived_at) return
                                    setActiveTab('projects')
                                    setCreateProjectRequest((request) => request + 1)
                                  }}
                                >
                                  <Folder className="mr-2 h-4 w-4" />
                                  Create Project
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleToggleArchiveTeam(selectedPod)}>
                                  <Archive className="mr-2 h-4 w-4" />
                                  {selectedPod.archived_at ? 'Unarchive team' : 'Archive team'}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                                  onClick={() => setDeleteTarget(selectedPod)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete team
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>

                        {/* Members | Projects tab bar for the selected pod, per Figma
                            (2026-09-29). Inactive tab is muted, active tab is
                            text-foreground with a primary underline -- standard
                            shadcn/Radix Tabs treatment, styled as an underline bar
                            instead of TabsList's default pill/segmented look. */}
                        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'members' | 'projects')}>
                          <TabsList className="h-auto w-full justify-start gap-4 rounded-none border-b border-border bg-transparent p-0">
                            <TabsTrigger
                              value="members"
                              className="gap-1.5 rounded-none border-b-2 border-transparent px-1 pb-2 text-muted-foreground shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
                            >
                              Members
                              <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                                {podMemberCounts.get(selectedPod.id) ?? 0}
                              </span>
                            </TabsTrigger>
                            <TabsTrigger
                              value="projects"
                              className="gap-1.5 rounded-none border-b-2 border-transparent px-1 pb-2 text-muted-foreground shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
                            >
                              Projects
                              <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                                {podProjectCounts.get(selectedPod.id) ?? 0}
                              </span>
                            </TabsTrigger>
                          </TabsList>
                        </Tabs>

                        {activeTab === 'members' && (
                        <>
                        <TableToolbar label="team members" query={memberSearch} onQueryChange={setMemberSearch} count={filteredPodMembers.length} total={podMembers.length}
                          active={!!memberSearch || memberRole !== 'all' || memberStatus !== 'all'} onClear={() => { setMemberSearch(''); setMemberRole('all'); setMemberStatus('all') }}>
                          <TableFilter label="Filter team member role" value={memberRole} onChange={setMemberRole} allLabel="All workspace roles" options={['owner','admin','manager','member'].map(value => ({ value, label: value.charAt(0).toUpperCase() + value.slice(1) }))} />
                          <TableFilter label="Filter sign-in status" value={memberStatus} onChange={setMemberStatus} allLabel="All statuses" options={[{ value: 'active', label: 'Active' }, { value: 'pending', label: 'Pending sign-in' }]} />
                        </TableToolbar>
                        {podMembers.length > 0 && filteredPodMembers.length === 0 && <p className="py-4 text-sm text-muted-foreground">No members match these filters.</p>}
                        {podMembers.length === 0 ? (
                          <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
                            <UserPlus className="h-8 w-8 text-muted-foreground" />
                            <p className="font-medium text-foreground">No members yet!</p>
                            <p className="max-w-sm text-sm text-muted-foreground">
                              This team doesn&apos;t have any members. Add people to start
                              collaborating with your team.
                            </p>
                            {isAdmin && (
                              <Button
                                type="button"
                                className="mt-2 gap-2"
                                disabled={!!selectedPod.archived_at}
                                title={selectedPod.archived_at ? 'Unarchive this team before inviting new members.' : undefined}
                                onClick={() => openInviteForPod(selectedPod)}
                              >
                                <UserPlus className="h-4 w-4" />
                                Add Slack members
                              </Button>
                            )}
                          </div>
                        ) : (
                          // Matches the reference mock's table: sits directly in the
                          // panel, no extra nested border box around it (the Card
                          // around this whole panel already provides that edge) --
                          // only the header/row hairlines below separate things.
                          // Full-bleed out of the panel's own p-4/sm:p-6 (same
                          // treatment as the pagination bar) so every row divider
                          // spans the panel's true width instead of stopping at the
                          // padded table edge (2026-09-29, per Figma).
                          <div className="-mx-4 overflow-x-auto sm:-mx-6">
                              <table className="w-full min-w-[640px] caption-bottom text-sm">
                                <thead className="[&_tr]:border-b">
                                  <tr className="border-b">
                                    <SortableHeader className="h-11 px-4 text-left align-middle font-medium text-muted-foreground" column="name" label="Member" sort={sort} onSort={toggleSort} />
                                    <SortableHeader className="h-11 px-4 text-left align-middle font-medium text-muted-foreground" column="role" label="Role" sort={sort} onSort={toggleSort} />
                                    <SortableHeader className="h-11 px-4 text-left align-middle font-medium text-muted-foreground" column="joined" label="Date Joined" sort={sort} onSort={toggleSort} />
                                    <SortableHeader className="h-11 px-4 text-left align-middle font-medium text-muted-foreground" column="status" label="Status" sort={sort} onSort={toggleSort} />
                                    {isAdmin && <th className="h-11 px-4" />}
                                  </tr>
                                </thead>
                                {/* No :last-child border-0 exception anymore -- every
                                    row, including the last, gets its own top+bottom
                                    divider (2026-09-29, per Figma). */}
                                <tbody>
                                  {pageMembers.map((m) => (
                                    <tr key={m.id} className="border-b hover:bg-muted/50">
                                      <td className="p-4 align-middle">
                                        <div className="flex items-center gap-3">
                                          <Avatar className="h-8 w-8">
                                            {m.avatar_url && <AvatarImage src={m.avatar_url} alt="" />}
                                            <AvatarFallback className="text-xs font-semibold">
                                              {initialsOf(displayName(m))}
                                            </AvatarFallback>
                                          </Avatar>
                                          <span className="font-medium text-foreground">
                                            {displayName(m)}
                                          </span>
                                        </div>
                                      </td>
                                      <td className="p-4 align-middle text-muted-foreground">
                                        <div className="flex flex-col items-start gap-1">
                                          <span>
                                            {m.title ||
                                              (m.role === 'owner' || m.id === ownerMemberId
                                                ? 'Owner'
                                                : m.role === 'admin'
                                                  ? 'Admin'
                                                  : m.auth_user_id && selectedPodManagerIds.has(m.auth_user_id)
                                                    ? 'Manager'
                                                    : 'Member')}
                                          </span>
                                          {isAdmin && m.can_manage && m.auth_user_id && m.role !== 'owner' && (isOwner || m.role !== 'admin') && (
                                            <Button
                                              variant="link"
                                              size="sm"
                                              className="h-auto min-h-0 p-0 text-xs font-normal"
                                              disabled={togglingManagerId === m.id}
                                              onClick={() => handleToggleManager(m)}
                                            >
                                              {togglingManagerId === m.id
                                                ? 'Saving…'
                                                : selectedPodManagerIds.has(m.auth_user_id)
                                                  ? 'Remove as manager'
                                                  : 'Make manager'}
                                            </Button>
                                          )}
                                        </div>
                                      </td>
                                      <td className="p-4 align-middle text-muted-foreground">
                                        {new Date(m.joined_at).toLocaleString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric',
                                          hour: 'numeric',
                                          minute: '2-digit',
                                        })}
                                      </td>
                                      <td className="p-4 align-middle">
                                        {/* Pill + dot, matching the reference mock exactly.
                                            Note on color: this app's semantic --success
                                            token is amber ("shipped/positive" per
                                            index.css), already used that way elsewhere
                                            (e.g. TeamCheckins' "on-time" badge) -- using it
                                            here for "Active" would render amber, not the
                                            green the mock shows. Using the raw emerald
                                            swatch instead, same raw-Tailwind-for-a-status-
                                            color precedent already set by NewDashboard's
                                            getSentimentStyling (bg-green-50/text-green-700
                                            for "positive" sentiment) rather than inventing
                                            a third color convention.
                                            Text stays "Pending sign-in", not the mock's
                                            "Invited": see this file's header comment -- no
                                            email is actually sent (Slack-picker add), so
                                            "Invited" would misstate what happened. */}
                                        {m.auth_user_id ? (
                                          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700">
                                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                            Active
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                                            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                                            Pending sign-in
                                          </span>
                                        )}
                                      </td>
                                      {isAdmin && (
                                        <td className="p-4 align-middle text-right">
                                          {m.can_manage && (
                                            <DropdownMenu>
                                              <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon" aria-label={`Actions for ${displayName(m)}`}>
                                                  <MoreHorizontal className="h-4 w-4" />
                                                </Button>
                                              </DropdownMenuTrigger>
                                              <DropdownMenuContent align="end" className="w-48">
                                                <DropdownMenuItem onClick={() => navigate(`/dashboard/team/${teamId}/standups/${m.user_id}`)}>
                                                  <UserRound className="mr-2 h-4 w-4" />
                                                  View Profile
                                                </DropdownMenuItem>
                                                {m.auth_user_id && selectedPod && m.role !== 'owner' && (isOwner || m.role !== 'admin') && (
                                                  <DropdownMenuSub>
                                                    <DropdownMenuSubTrigger>
                                                      <Pencil className="mr-2 h-4 w-4" />
                                                      Edit role
                                                    </DropdownMenuSubTrigger>
                                                    <DropdownMenuSubContent className="w-40">
                                                      {(['member', 'manager', 'admin'] as TeamRole[]).map((role) => {
                                                const currentRole = m.role === 'owner' || m.role === 'admin'
                                                  ? m.role
                                                          : selectedPodManagerIds.has(m.auth_user_id!)
                                                            ? 'manager'
                                                            : 'member'
                                                        // A team must always keep at least one admin -- the manage-pods
                                                        // update_role action refuses this server-side too, but we also
                                                        // disable it here so the option doesn't look clickable and then
                                                        // fail. Covers both the sole admin demoting themselves and (in
                                                        // principle) demoting the last other admin.
                                                        const wouldRemoveLastAdmin = currentRole === 'admin' && role !== 'admin' && adminCount <= 1
                                                        return (
                                                          <DropdownMenuItem
                                                            key={role}
                                                            disabled={roleUpdatingId === m.id || currentRole === role || wouldRemoveLastAdmin}
                                                            onClick={() => handleUpdateMemberRole(m, role)}
                                                            title={wouldRemoveLastAdmin ? 'This team needs at least one admin. Promote someone else to admin first.' : undefined}
                                                          >
                                                            <span className="mr-2 w-3 text-center">{currentRole === role ? '✓' : ''}</span>
                                                            {role.charAt(0).toUpperCase() + role.slice(1)}
                                                            {wouldRemoveLastAdmin && <span className="ml-auto text-xs text-muted-foreground">Last admin</span>}
                                                          </DropdownMenuItem>
                                                        )
                                                      })}
                                                    </DropdownMenuSubContent>
                                                  </DropdownMenuSub>
                                                )}
                                                {m.role !== 'owner' && (isOwner || m.role !== 'admin') && (
                                                  <>
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem
                                                      className="text-destructive focus:text-destructive"
                                                      onClick={() => setRemoveTarget(m)}
                                                    >
                                                      <Trash2 className="mr-2 h-4 w-4" />
                                                      Remove member
                                                    </DropdownMenuItem>
                                                  </>
                                                )}
                                              </DropdownMenuContent>
                                            </DropdownMenu>
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                        )}
                        </>
                        )}

                        <ProjectsPanel
                          teamId={teamId}
                          pod={selectedPod}
                          pods={pods}
                          isAdmin={isAdmin}
                          active={activeTab === 'projects'}
                          onCountChange={handleProjectCountChange}
                          createRequest={createProjectRequest}
                          members={members}
                        />
                      </>
                    )}
                  </div>

                  {/* Pagination is a sibling of the flex-1 content div above, not
                      nested inside it -- so it's pinned flush to this panel's own
                      bottom edge by the flex column, even when the equal-height grid
                      row stretches this panel taller than its content (2026-09-29,
                      fixes the gap the padded-block version left underneath it). */}
                  {selectedPod && activeTab === 'members' && podMembers.length > 0 && (
                    // [prev][Page X of Y][next], matching the reference mock exactly --
                    // plain icons (no button box/border), divided by vertical rules
                    // that continue the table's grid lines; prev is faint when
                    // disabled, next stays bold/dark when there's another page.
                    <div className="flex items-stretch divide-x divide-border border-t border-border">
                      <div className="flex-1" />
                      <button
                        type="button"
                        aria-label="Previous page"
                        disabled={currentPage === 0}
                        onClick={() => setPage(Math.max(currentPage - 1, 0))}
                        className="flex w-16 items-center justify-center py-[18px] disabled:cursor-not-allowed"
                      >
                        <ChevronLeft
                          className={cn('h-5 w-5', currentPage === 0 ? 'text-muted-foreground/40' : 'text-foreground')}
                        />
                      </button>
                      <div className="flex w-40 items-center justify-center py-[18px] text-sm text-muted-foreground">
                        Page {currentPage + 1} of {totalPages}
                      </div>
                      <button
                        type="button"
                        aria-label="Next page"
                        disabled={currentPage + 1 >= totalPages}
                        onClick={() => setPage(Math.min(currentPage + 1, totalPages - 1))}
                        className="flex w-16 items-center justify-center py-[18px] disabled:cursor-not-allowed"
                      >
                        <ChevronRight
                          className={cn(
                            'h-5 w-5',
                            currentPage + 1 >= totalPages ? 'text-muted-foreground/40' : 'text-foreground'
                          )}
                          strokeWidth={2.5}
                        />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </main>
      </SidebarInset>

      {invitePodId && isAdmin && <AddSlackMembersSheet
        key={`${teamId}:${invitePodId}`} teamId={teamId} initialPodId={invitePodId}
        pods={pods} isOwner={isOwner} onClose={() => setInvitePodId(null)} onSaved={loadAll}
      />}

      <Sheet open={createOpen} onOpenChange={(open) => {
        if (creating) return
        setCreateOpen(open)
        if (!open) { setCreateSelectedIds(new Set()); setCreateMoveConfirmed(false); setCreateName(''); setCreateDescription('') }
      }}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Create team</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Team name</label>
              <Input
                placeholder="e.g, Design"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Description</label>
              <textarea
                className="min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Type here"
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Add Members</label>
              <MemberPicker options={memberOptions} selected={createSelectedIds} disabled={creating}
                onChange={ids => { setCreateSelectedIds(ids); setCreateMoveConfirmed(false) }} />
              <MemberMoveConfirmation moves={createMoves} confirmed={createMoveConfirmed} onChange={setCreateMoveConfirmed} disabled={creating} />
            </div>
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={() => { setCreateOpen(false); setCreateSelectedIds(new Set()); setCreateMoveConfirmed(false); setCreateName(''); setCreateDescription('') }} disabled={creating}>
              Cancel
            </Button>
            <Button disabled={!createName.trim() || creating || (!!createMoves.length && !createMoveConfirmed)} onClick={handleCreateTeam}>
              <Plus className="mr-1.5 h-4 w-4" />
              {creating ? 'Creating…' : 'Create Team'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Sheet open={!!renameTarget} onOpenChange={(open) => !open && !renaming && setRenameTarget(null)}>
        <SheetContent className="flex h-full w-full flex-col gap-0 p-0 sm:max-w-lg">
          <SheetHeader className="border-b px-8 py-8">
            <SheetTitle className="text-2xl">Edit team</SheetTitle>
          </SheetHeader>
          {renameTarget && (
            <div className="min-h-0 flex-1 overflow-y-auto px-8 py-8">
              <div className="space-y-7">
                <div className="space-y-2">
                  <label className="text-base font-medium text-foreground">Team name</label>
                  <Input
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleRenameSubmit()
                    }}
                    maxLength={60}
                    autoFocus
                    className="h-14 text-base"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-base font-medium text-foreground">Description</label>
                  <textarea
                    className="min-h-[88px] w-full resize-none rounded-md border border-input bg-background px-3 py-3 text-base placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    placeholder="Add a description"
                    value={renameDescription}
                    onChange={(e) => setRenameDescription(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-base font-medium text-foreground">Add Members</label>
                  <MemberPicker key={renameTarget.id} options={memberOptions} selected={editMemberIds} disabled={renaming}
                    onChange={ids => { setEditMemberIds(ids); setEditMoveConfirmed(false) }} />
                  <MemberMoveConfirmation moves={editMoves} confirmed={editMoveConfirmed} onChange={setEditMoveConfirmed} disabled={renaming} />
                </div>
              </div>
            </div>
          )}
          <SheetFooter className="border-t px-8 py-6 sm:justify-between">
            <Button variant="outline" onClick={() => setRenameTarget(null)} disabled={renaming}>
              Cancel
            </Button>
            <Button
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={handleRenameSubmit}
              disabled={renaming || !renameValue.trim() || (!!editMoves.length && !editMoveConfirmed)}
            >
              {renaming ? 'Saving…' : 'Save Changes'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Members will become unassigned, not removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingTeam}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: 'destructive' })}
              disabled={deletingTeam}
              onClick={(e) => {
                e.preventDefault()
                handleConfirmDeleteTeam()
              }}
            >
              {deletingTeam ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!transferTargetAuthId} onOpenChange={(open) => !open && setTransferTargetAuthId('')}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Transfer workspace ownership?</AlertDialogTitle>
            <AlertDialogDescription>
              The selected admin will become the owner. You will remain an admin and the new owner can later change your access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Select value={transferTargetAuthId} onValueChange={setTransferTargetAuthId}>
            <SelectTrigger aria-label="New workspace owner">
              <SelectValue placeholder="Select an admin" />
            </SelectTrigger>
            <SelectContent>
              {adminMembers.map((member) => (
                <SelectItem key={member.auth_user_id} value={member.auth_user_id!}>
                  {displayName(member)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={transferringOwnership}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: 'default' })}
              disabled={transferringOwnership || !transferTargetAuthId}
              onClick={(event) => {
                event.preventDefault()
                void handleTransferOwnership()
              }}
            >
              {transferringOwnership ? 'Transferring…' : 'Confirm transfer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!removeTarget} onOpenChange={(open) => !open && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove {removeTarget ? displayName(removeTarget) : 'this member'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              They will be removed from {selectedPod?.name ?? 'this team'}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removingMember}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: 'destructive' })}
              disabled={removingMember}
              onClick={(e) => {
                e.preventDefault()
                handleConfirmRemoveMember()
              }}
            >
              {removingMember ? 'Removing…' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarProvider>
  )
}

export default TeamManagement
